import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import type { ShipmentLocation } from '@fv/client-types'
import type { Workflow } from '@fv/models'

import CheckboxField from '@/components/inputs/CheckboxField'

import { toLoadHandlingUnitFormModel } from '../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../commodities/load-items/types'
import { useHandlingUnitsMultiTruck } from '../commodities/useHandlingUnits'
import { useRecentShipment } from '../quote/useRecentShipment'
import { useAppPreferences } from '../settings/user-preferences/hooks'
import type { ShipmentEquipmentFormModel } from '../shipment-equipment-form/types'

type AutofillHandler<T> = (recentValue: T, enabled: boolean) => void

type AutofillHandlers = {
  origin?: AutofillHandler<ShipmentLocation | undefined>
  destination?: AutofillHandler<ShipmentLocation | undefined>
  product?: AutofillHandler<LoadHandlingUnitFormModel[]>
  equipment?: AutofillHandler<ShipmentEquipmentFormModel[]>
  stops?: AutofillHandler<ShipmentLocation[]>
}

type AutofillField = keyof AutofillHandlers

const fieldOrder: AutofillField[] = [
  'origin',
  'destination',
  'product',
  'equipment',
  'stops',
]

const labels: Record<AutofillField, string> = {
  origin: 'Origin',
  destination: 'Destination',
  product: 'Product',
  equipment: 'Equipment',
  stops: 'Stops',
}

type Props = {
  workflow: Workflow
  // A selected preset wins: boxes are unchecked & disabled, and the initial autofill is skipped
  selectedTemplateId?: string
  /** Leaves the boxes unchecked and skips the initial autofill. */
  skipInitialAutofill?: boolean
  handlers: AutofillHandlers
}

export const AutofillPreviousShipment = ({
  workflow,
  selectedTemplateId,
  skipInitialAutofill,
  handlers,
}: Props) => {
  const prefs = useAppPreferences()
  const recentLoadQuery = useRecentShipment(workflow)
  const recentLoads = recentLoadQuery.data
  const recentLoad = recentLoads?.[0]
  const recentUnitsQuery = useHandlingUnitsMultiTruck(recentLoads)
  const recentUnits = recentUnitsQuery.data
  const hasAutofilled = useRef(false)
  const fields = fieldOrder.filter(field => handlers[field])

  const [checked, setChecked] = useState<
    Partial<Record<AutofillField, boolean>>
  >(() =>
    skipInitialAutofill
      ? {}
      : Object.fromEntries(
          fields.map(field => [
            field,
            !!prefs[
              `${workflow}_quote_remember_${field}` as keyof typeof prefs
            ],
          ]),
        ),
  )

  const fill = (field: AutofillField, enabled: boolean) => {
    switch (field) {
      case 'origin':
      case 'destination':
        return handlers[field]?.(
          recentLoad?.locations.find(l => l.type === field),
          enabled,
        )
      case 'product':
        return handlers.product?.(
          (recentLoad?.items ?? []).map(i =>
            toLoadHandlingUnitFormModel(
              i,
              recentUnits?.find(u => u._id === i.handlingUnitId),
            ),
          ),
          enabled,
        )
      case 'equipment':
        return handlers.equipment?.(
          (recentLoads ?? []).map(
            l => l.equipment as ShipmentEquipmentFormModel,
          ),
          enabled,
        )
      case 'stops':
        return handlers.stops?.(recentLoad?.locations ?? [], enabled)
    }
  }

  useEffect(() => {
    if (selectedTemplateId) setChecked({})
  }, [selectedTemplateId])

  useEffect(() => {
    if (!recentLoad || recentUnitsQuery.isLoading || hasAutofilled.current)
      return
    hasAutofilled.current = true
    if (selectedTemplateId) return
    fields.filter(field => checked[field]).forEach(field => fill(field, true))
  })

  const handleChange = (field: AutofillField, enabled: boolean) => {
    if (enabled && !recentLoad) {
      toast.error(`You haven't created any shipments yet`, {
        id: 'recent-shipment',
      })
      return
    }
    setChecked(prev => ({ ...prev, [field]: enabled }))
    fill(field, enabled)
  }

  return (
    <div className="flex items-center space-x-4">
      <div>Autofill from last shipment &gt;</div>
      {fields.map(field => (
        <CheckboxField
          key={field}
          name={`autofill-${field}`}
          alternate
          checked={!!checked[field]}
          disabled={!!selectedTemplateId}
          label={labels[field]}
          onChange={e => handleChange(field, e.target.checked)}
        />
      ))}
    </div>
  )
}
